import React, { useContext, useMemo, useState } from 'react';

import { Row, Col } from 'antd';
import Axios from 'axios';
import { Formik, Field } from 'formik';

import styled from 'styled-components';

import { validateAmountZeroAllowed } from '@libs/NwUtils';

import { ListContext } from '@contexts/ListContext';

import { forceReloadProjectTransactions } from '@hooks/API/Project/UseProjectTransactions';
import { forceReloadTransaction } from '@hooks/API/UseTransaction';
import useConfigurations from '@hooks/Configurations/UseConfigurations';

import CustomSwitch from '@components/Gui/CustomSwitch';
import { NwCancelButton, NwSaveButton } from '@components/Gui/NwButton';
import { NWAmountInput, NWQuantitySelector } from '@components/Gui/NwForm/NwFormItems';
import { NwForm, NwFormButtonsBar } from '@components/Gui/NwForm/NwFormWrapper';

const Wrapper = styled.div`
    .payroll {
        background-color: ${(props) => props.theme.LighterBorderColor};
        padding: 0.5rem 1rem 0.75rem 1rem;
        border-radius: 4px;
        border: ${(props) => props.theme.DefaultBorder};
        margin: 0.5rem 0;
        span {
            font-size: ${(props) => props.theme.FontSizeSm};
            button {
                margin-right: 1rem;
            }
        }
        .payroll-amount {
            display: block;
            margin: 0.5rem 1rem 0 4rem;
        }
        .payroll-not-allowed {
            color: ${(props) => props.theme.DangerColor};
            font-size: ${(props) => props.theme.FontSizeSm};
            margin: 0.15rem 1rem 0 4rem;
        }
    }
`;

const TransactionAmountForm = ({ transaction, onSave, onCancel }) => {
    const configurations = useConfigurations();
    const { cachedList } = useContext(ListContext);

    const transactionFamilies = cachedList.transactionFamilies.items;
    const rateTransaction = transactionFamilies.find((family) => family.Code === 'JobFee');
    const currentLabel = rateTransaction.TransactionLabels.find((label) => label.ID === transaction.TransactionLabelID);

    const hasClientAdvance = useMemo(() => {
        if (transaction.Family === 'JobFee') {
            return transaction.ClientStatements.some((cs) => cs.isClientAdvance);
        }
        return false;
    }, [transaction.ClientStatements]);

    const payrollAllowed =
        transaction.Family === 'JobFee' &&
        transaction.Currency === configurations.defaultCurrency &&
        !currentLabel.FixedTarif;

    const hasExternalFee = transaction.Family === 'CommAsMothAg';
    const hasQuantity =
        transaction.Family !== 'CommAsMothAg' && transaction.Family !== 'AgFee' && transaction.Family !== 'JobCredit';

    const getSingleFeeLabel = () => {
        if (hasQuantity) {
            return 'Single Fee';
        }
        if (transaction.Family === 'CommAsMothAg') {
            return 'Commission Amount';
        }
        return 'Amount';
    };

    const [touchForm, setTouchForm] = useState(false);
    const [usePayroll, setUsePayroll] = useState(hasClientAdvance);

    let currentClientAdvance = 0;
    if (hasClientAdvance) {
        currentClientAdvance = Math.abs(transaction.ClientStatements.find((cs) => cs.isClientAdvance).Amount);
    }

    let initialValues = {
        CurrencySingleFee: transaction
            ? [transaction.Currency, transaction.SingleFee]
            : [configurations.defaultCurrency, 0],
        QuantityTypeQuantity: transaction ? [transaction.Quantity, transaction.TypeQuantity] : [1, 'Units'],
        ClientAdvance: [configurations.defaultCurrency, currentClientAdvance],
        ExternalFee: transaction
            ? [transaction.Currency, transaction.ExternalFee]
            : [configurations.defaultCurrency, 0],
    };

    const submitForm = async (values, setStatus, setSubmitting) => {
        setSubmitting(true);
        const dataToSend = {
            TransactionID: transaction.ID,
            Amount: values.CurrencySingleFee[1],
            Quantity: values.QuantityTypeQuantity[0],
            TypeQty: values.QuantityTypeQuantity[1],
            ClientAdvance: usePayroll ? values.ClientAdvance[1] : 0,
        };
        if (hasExternalFee && values.ExternalFee[1] && values.ExternalFee[1] > 0) {
            dataToSend.ExternalFee = values.ExternalFee[1];
        }

        try {
            await Axios.put(`/transactions/${transaction.Family}/modifyamount`, dataToSend);
            forceReloadTransaction(transaction.ID);
            if (transaction.ProjectID) {
                forceReloadProjectTransactions(transaction.ProjectID);
            }
            setTouchForm(false);
            setSubmitting(false);
            onSave();
        } catch (error) {
            setSubmitting(false);
            console.error('errore', error);
        }
    };

    return (
        <Wrapper>
            <Formik
                initialValues={initialValues}
                enableReinitialize={true}
                onSubmit={(values, { setStatus, setSubmitting }) => {
                    setStatus();
                    submitForm(values, setStatus, setSubmitting);
                }}
            >
                {({ isSubmitting, values, handleSubmit }) => {
                    return (
                        <NwForm values={values} onTouchForm={setTouchForm} onFinish={handleSubmit} layout="vertical">
                            <Row gutter={16}>
                                <Col lg={12} md={12} sm={12} xs={24}>
                                    <Field
                                        component={NWAmountInput}
                                        value={values.CurrencySingleFee}
                                        currencyReadOnly={true}
                                        validate={validateAmountZeroAllowed}
                                        label={getSingleFeeLabel()}
                                        name="CurrencySingleFee"
                                    />
                                </Col>
                                {hasQuantity && (
                                    <Col lg={12} md={12} sm={12} xs={24}>
                                        <Field
                                            component={NWQuantitySelector}
                                            value={values.QuantityTypeQuantity}
                                            label="Quantity"
                                            name="QuantityTypeQuantity"
                                        />
                                    </Col>
                                )}
                            </Row>
                            {payrollAllowed && (
                                <div className="payroll">
                                    <CustomSwitch
                                        text={`Payroll / Client Advance`}
                                        checked={usePayroll}
                                        onChange={(value) => setUsePayroll(value)}
                                    />
                                    {usePayroll && (
                                        <div className="payroll-amount">
                                            <Field
                                                component={NWAmountInput}
                                                value={values.ClientAdvance}
                                                label=""
                                                name="ClientAdvance"
                                                fixedCurrency={configurations.defaultCurrency}
                                            />
                                        </div>
                                    )}
                                </div>
                            )}
                            {hasExternalFee && (
                                <div className="external-fee">
                                    <Field
                                        component={NWAmountInput}
                                        value={values.ExternalFee}
                                        label="Model Fee"
                                        name="ExternalFee"
                                        currencyReadOnly={true}
                                    />
                                </div>
                            )}
                            <NwFormButtonsBar
                                marginTop="3rem"
                                left={
                                    <NwCancelButton
                                        disabled={isSubmitting}
                                        onClick={() => {
                                            setTouchForm(false);
                                            onCancel();
                                        }}
                                    />
                                }
                                right={
                                    <NwSaveButton htmlType="submit" disabled={isSubmitting} loading={isSubmitting} />
                                }
                            />
                        </NwForm>
                    );
                }}
            </Formik>
        </Wrapper>
    );
};

export default TransactionAmountForm;
